import { Box, useThemeColor } from '@wistia/vhs';
import omit from 'lodash/omit';
import { styled } from 'styled-components';
import type { Maybe, SocialMediaUrls } from '~/graphql';
import { isEmptyRecord, isNil, isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import type { JSX } from 'react';
import type { SpeakerBioGridItemStyleStrategy } from '~/components/SpeakerBios/components/SpeakerBioStyles';
import { StyleVariableFactories } from '~/components/SpeakerBios/theming/StyleVariableFactories';
import { trackStructEvent } from '~/utilities/snowplow';
import { SocialMediaSvg } from './SocialMediaSvg';

const Link = styled.a`
  &:hover {
    opacity: 0.8;
  }
`;

type Props = {
  links: Maybe<SocialMediaUrls>;
  styleStrategy?: SpeakerBioGridItemStyleStrategy;
};

export const SpeakerSocialMediaLinks = ({ links, styleStrategy }: Props): JSX.Element | null => {
  const colorTheme = useThemeColor();

  if (isEmptyRecord(links) || isNil(links)) {
    return null;
  }

  const styleVariableFactories = StyleVariableFactories.create(colorTheme);

  const handleClick = () => {
    trackStructEvent({
      category: 'link',
      action: 'click',
      label: 'speaker-bio-interactions',
      property: 'type',
      value: 'social',
    });
  };

  return (
    <Box
      gap="0.5rem"
      justifyContent="center"
    >
      {Object.keys(omit(links, '__typename')).map((key) => {
        const href = links[key] as string;

        return isNonEmptyString(href) && isNotNil(href) ? (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={key}
            onClick={handleClick}
          >
            <SocialMediaSvg
              name={key}
              fillColor={styleVariableFactories.socialMediaSvgFillColor(styleStrategy)}
              strokeColor={styleVariableFactories.socialMediaSvgStrokeColor(styleStrategy)}
            />
          </Link>
        ) : null;
      })}
    </Box>
  );
};
